import React, { useMemo } from "react"
import useDebounceQueryParams from "magik-react-hooks/useRouterDebounceQueryParams"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import { useRunRj } from "react-rocketjump"
import { ProdottoState } from "../../state/prodotti"
import Layout from "../../components/Layout/Layout"
import ModalFormProdotto from "../../components/Prodotti/ModalFormProdotto"
import ModalDelete from "../../components/Modals/ModalDelete"
import ListProdotti from "../../components/Prodotti/ListProdotti"
import qs from "query-string"
import {
  TextField,
  Button,
  Card,
  CircularProgress,
  Grid,
  InputAdornment,
  IconButton,
} from "@material-ui/core"
import { RotateLeft, AddCircle, GetApp, Cancel } from "@material-ui/icons"
import { useExportToken } from "../../state/exportToken"
import { useSelectedAzienda } from "../../SelectedAzienda"
import { isMobile } from "react-device-detect"
import { useAuthUser } from "use-eazy-auth"

export default function Prodotti() {
  const [{ selectedAzienda }] = useSelectedAzienda()
  const [
    queryParams,
    setQueryParams,
    debQueryParams,
    setDebQueryParams,
  ] = useDebounceQueryParams()

  const [modalCreate, actionsModalCreate] = useModalTrigger()
  const [modalUpdate, actionsModalUpdate] = useModalTrigger()
  const [modalDelete, actionsModalDelete] = useModalTrigger()
  const { value: deleteProdotto } = modalDelete

  const onUpdate = (prodotto) => actionsModalUpdate.open(prodotto)
  const onDelete = (prodotto) => actionsModalDelete.open(prodotto)

  const filters = useMemo(() => {
    return { ...debQueryParams, azienda: selectedAzienda.id }
  }, [debQueryParams, selectedAzienda])

  const [
    { list, loading, pagination },
    { addProdotto, updateProdotto, removeProdotto, refresh },
  ] = useRunRj(ProdottoState, [filters], false)

  const columnProps = {
    value: queryParams.ordering,
    onChange: (value) => setDebQueryParams({ ordering: value }),
  }

  const isProductionEnv = process.env.NODE_ENV === "production"
  const host = isProductionEnv ? window.location.host : "localhost:8000"
  const protocol = window.location.protocol

  const exportToken = useExportToken()

  const linkCsv = useMemo(() => {
    return `${protocol}//${host}/api/prodotti-export/csv?${qs.stringify({
      ...filters,
      export_token: exportToken,
    })}`
  }, [exportToken, filters, host, protocol])

  const linkPdf = useMemo(() => {
    return `${protocol}//${host}/api/prodotti-export/pdf?${qs.stringify({
      ...filters,
      export_token: exportToken,
    })}`
  }, [exportToken, filters, host, protocol])

  const { user } = useAuthUser()

  const isNotEmployee = useMemo(() => {
    if (user.profilo && user.profilo.permessi !== "employee") {
      return true
    }
  }, [user.profilo])

  return (
    <Layout title="Magazzino">
      <div>
        <Card className="card-filters">
          <Grid container>
            <Grid item xs={12} md={6}>
              <div>
                <TextField
                  size="small"
                  placeholder="Cerca..."
                  value={queryParams.search ?? ""}
                  onChange={(e) =>
                    setDebQueryParams({
                      search: e.target.value,
                      page: 1,
                    })
                  }
                  style={{ width: isMobile ? "100%" : undefined }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          disabled={!queryParams.search}
                          aria-label="toggle password visibility"
                          onClick={() => setQueryParams(() => {})}
                          edge="end"
                        >
                          <Cancel
                            color={!queryParams.search ? undefined : "primary"}
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  id="outlined-basic"
                  variant="outlined"
                />
              </div>
            </Grid>
            {isNotEmployee && (
              <Grid item md={6} xs={12}>
                <div
                  className={
                    isMobile
                      ? "d-flex justify-content-center pt-5"
                      : "text-right"
                  }
                >
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<GetApp />}
                    href={linkCsv}
                  >
                    Excel
                  </Button>
                  <Button
                    style={{ marginLeft: 10 }}
                    variant="contained"
                    color="primary"
                    target="_blank"
                    startIcon={<GetApp />}
                    href={linkPdf}
                  >
                    Pdf
                  </Button>
                </div>
              </Grid>
            )}
          </Grid>
        </Card>
        <Card className="card-add">
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddCircle />}
            onClick={() => actionsModalCreate.open()}
          >
            Aggiungi Prodotto
          </Button>
        </Card>
        {list === null && loading && (
          <div style={{ textAlign: "center" }}>
            <CircularProgress color="primary" />
          </div>
        )}
        {list && (
          <ListProdotti
            columnProps={columnProps}
            pagination={pagination}
            prodotti={list}
            onUpdate={onUpdate}
            onDelete={onDelete}
          />
        )}
        <ModalFormProdotto
          save={(prodotto) => {
            return addProdotto
              .onSuccess(() => { 
                actionsModalCreate.close()
                })
              .asPromise({ ...prodotto, azienda: selectedAzienda.id })
          }}
          isOpen={modalCreate.isOpen}
          toggle={actionsModalCreate.toggle}
          onExited={actionsModalCreate.onClosed}
        />
        <ModalFormProdotto
          save={(prodotto) => {
            return updateProdotto
              .onSuccess(() => actionsModalUpdate.close())
              .asPromise({ ...prodotto, azienda: selectedAzienda.id })
          }}
          prodotto={modalUpdate.value}
          isOpen={modalUpdate.isOpen}
          toggle={actionsModalUpdate.toggle}
          onExited={() => {
            actionsModalUpdate.onClosed()
          }}
        />
        <ModalDelete
          isOpen={modalDelete.isOpen}
          toggle={actionsModalDelete.toggle}
          title={
            deleteProdotto ? `Rimuovere ${deleteProdotto.prodotto}?` : null
          }
          text={
            deleteProdotto
              ? `Sei sicuro di voler eliminare il prodotto ${deleteProdotto.prodotto}?`
              : null
          }
          onDelete={() => {
            return removeProdotto
              .onSuccess(() => actionsModalDelete.close())
              .asPromise(modalDelete.value)
          }}
          onExited={actionsModalDelete.onClosed}
        />
      </div>
    </Layout>
  )
}
